/** @jsxImportSource @emotion/react */
import { css } from '@emotion/react';
import {
  Button,
  Select,
  Switch,
  Textarea,
  TextInput,
  TextToggle,
  TextToggleGroup,
  getColor,
  vars,
} from '@flodesk/grain';
import TextInputWithSuffix from '@/components/ui/TextInputWithSuffix';
import { AnimatePresence, motion } from 'framer-motion';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import cardsImg from './cards.png';
import DevicesIcon from './Devices.svg';
import wandPatternImg from './wand-pattern-pack.jpg';

import thumbWand from './thumb-wand.jpg';
import thumbCrown from './thumb-crown.jpg';
import thumbItinerary from './thumb-itinerary.jpg';
import thumbMinutes from './thumb-minutes.jpg';
import thumbMarrakech from './thumb-marrakech.jpg';

// ─── Types ───────────────────────────────────────────────────────────────────

type PreviewState = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7;

const PRODUCTS = [
  {
    name: 'Wand Pattern Pack - 24 TOTAL',
    price: '$12',
    stock: '\u221E',
    sold: '0',
    thumb: thumbWand,
  },
  { name: 'The Birthday Crown Kit', price: '$20', stock: '180', sold: '165', thumb: thumbCrown },
  {
    name: 'Custom itinerary plus',
    price: '$450',
    stock: '\u221E',
    sold: '22',
    thumb: thumbItinerary,
  },
  {
    name: '30 EXTRA MINUTES to chat about your trip',
    price: '$27',
    stock: '\u221E',
    sold: '1055',
    thumb: thumbMinutes,
  },
  {
    name: 'the DIY Marrakech Bundle',
    price: '$50',
    stock: '\u221E',
    sold: '8',
    thumb: thumbMarrakech,
  },
];

// ─── Shared layout styles ─────────────────────────────────────────────────────

const sectionStyle = css`
  background: white;
  border-radius: 16px;
  padding: 32px 40px 40px;
  box-shadow:
    0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
    0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05);

  /* ensure grain fields have proper label-to-field gap */
  label[class*='FieldLabel'],
  [class*='FieldLabelRoot'] {
    margin-bottom: 8px;
  }
`;

const sectionTitleStyle = css`
  font-size: 24px;
  font-weight: 500;
  margin-bottom: 24px;
  color: ${getColor(vars.colors.content)};
`;

const separatorStyle = css`
  border: none;
  border-top: 1px solid ${getColor(vars.colors.border)};
  margin-bottom: 24px;
`;

const rowBetweenStyle = css`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

// ─── Image upload area ────────────────────────────────────────────────────────

type ImageUploadProps = {
  image: string | null;
  height?: number;
  circle?: boolean;
  onUpload: (dataUrl: string) => void;
  inputRef: React.RefObject<HTMLInputElement>;
};

function ImageUpload({
  image,
  height = 200,
  circle = false,
  onUpload,
  inputRef,
}: ImageUploadProps) {
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = ev => onUpload(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  return (
    <div
      role="button"
      tabIndex={0}
      onClick={() => inputRef.current?.click()}
      onKeyDown={e => (e.key === 'Enter' || e.key === ' ') && inputRef.current?.click()}
      css={css`
        width: 100%;
        height: ${height}px;
        border: 1px dashed ${getColor(vars.colors.border)};
        border-radius: 10px;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        gap: 8px;
        position: relative;
        cursor: pointer;
        overflow: hidden;
        ${image
          ? `border-style: solid; border-color: transparent; background: ${getColor(vars.colors.background3)};`
          : ''}
      `}
    >
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        css={css`
          display: none;
        `}
        onChange={handleChange}
      />
      {image && circle && (
        <div
          css={css`
            width: 200px;
            height: 200px;
            border-radius: 50%;
            overflow: hidden;
          `}
        >
          <img
            src={image}
            alt=""
            css={css`
              width: 100%;
              height: 100%;
              object-fit: cover;
            `}
          />
        </div>
      )}
      {image && !circle && (
        <img
          src={image}
          alt=""
          css={css`
            position: absolute;
            inset: 0;
            width: 100%;
            height: 100%;
            object-fit: cover;
          `}
        />
      )}
      {!image && (
        <>
          <div
            css={css`
              width: 36px;
              height: 36px;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
              <path
                d="M8 1V12M8 1L4 5M8 1L12 5"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
              <path
                d="M1 13V15C1 15.5523 1.44772 16 2 16H14C14.5523 16 15 15.5523 15 15V13"
                stroke={getColor(vars.colors.content)}
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <p
            css={css`
              font-size: 14px;
              font-weight: 500;
              color: ${getColor(vars.colors.content)};
            `}
          >
            Upload image
          </p>
          <p
            css={css`
              font-size: 14px;
              color: ${getColor(vars.colors.content2)};
            `}
          >
            Max image size 10mb
          </p>
        </>
      )}
    </div>
  );
}

// ─── Toggle row (label + hint on left, Switch on right) ───────────────────────

type ToggleRowProps = {
  id: string;
  label: string;
  hint?: string;
  active: boolean;
  onChange: (v: boolean) => void;
};

function ToggleRow({ id, label, hint, active, onChange }: ToggleRowProps) {
  return (
    <div css={rowBetweenStyle}>
      <div
        css={css`
          flex: 1;
          padding-right: 48px;
        `}
      >
        <p
          css={css`
            font-size: 14px;
            font-weight: 500;
            color: ${getColor(vars.colors.content)};
          `}
        >
          {label}
        </p>
        {hint && (
          <p
            css={css`
              font-size: 14px;
              color: ${getColor(vars.colors.content2)};
            `}
          >
            {hint}
          </p>
        )}
      </div>
      <Switch id={id} isActive={active} onChange={() => onChange(!active)} />
    </div>
  );
}

// ─── Source select (image source dropdown) ────────────────────────────────────

const imageSourceOptions = [
  { value: 'computer', content: 'My computer' },
  { value: 'unsplash', content: 'Unsplash' },
  { value: 'giphy', content: 'Giphy' },
  { value: 'canva', content: 'Canva' },
];

// ─── Right panel preview cards ────────────────────────────────────────────────

function HeroCard() {
  return (
    <div
      css={css`
        position: absolute;
        inset: 0;
      `}
    >
      <div
        css={css`
          position: absolute;
          top: 100px;
          left: 0;
          right: 0;
          text-align: center;
        `}
      >
        <h1
          css={css`
            font-size: 30px;
            font-weight: 500;
            line-height: 34px;
            color: ${getColor(vars.colors.content)};
          `}
        >
          Create your product
          <br />
          and start selling everywhere
        </h1>
      </div>
      <div
        css={css`
          position: absolute;
          inset: 0;
          display: flex;
          align-items: center;
          justify-content: center;
          top: 218px;
        `}
      >
        <img
          src={cardsImg}
          alt=""
          css={css`
            max-width: 90%;
            height: auto;
          `}
        />
      </div>
    </div>
  );
}

type ProductCardProps = {
  name: string;
  desc: string;
  image: string | null;
  price: string;
  salePrice: string;
  onSale: boolean;
  isRecurring: boolean;
};

function ProductCard({
  name,
  desc,
  image,
  price,
  salePrice,
  onSale,
  isRecurring,
}: ProductCardProps) {
  const hasSale = onSale && price && salePrice;
  const formatPrice = (p: string) => (p.startsWith('$') ? p : `$${p}`);
  const priceStr = price ? formatPrice(price) : '';
  const salePriceStr = salePrice ? formatPrice(salePrice) : '';

  return (
    <div
      css={css`
        background: white;
        border-radius: 16px;
        padding: 24px;
        width: 320px;
        border: 1px solid ${getColor(vars.colors.border)};
        box-shadow:
          0 6px 14px rgba(0, 0, 0, 0.09),
          0 2px 3px rgba(0, 0, 0, 0.05);
      `}
    >
      <div
        css={css`
          width: 272px;
          height: 272px;
          border-radius: 10px;
          margin-bottom: 16px;
          overflow: hidden;
          background: ${getColor(vars.colors.background2)};
        `}
      >
        {image && (
          <img
            src={image}
            alt=""
            css={css`
              width: 100%;
              height: 100%;
              object-fit: cover;
            `}
          />
        )}
      </div>
      <p
        css={css`
          font-size: 16px;
          font-weight: 500;
          margin-bottom: 4px;
          color: ${getColor(vars.colors.content)};
        `}
      >
        {name || 'Your product'}
      </p>
      <p
        css={css`
          font-size: 14px;
          line-height: 20px;
          margin-bottom: 12px;
          color: ${getColor(vars.colors.content2)};
        `}
      >
        {desc}
      </p>
      {price && (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 8px;
          `}
        >
          {hasSale && (
            <span
              css={css`
                font-size: 18px;
                font-weight: 500;
                color: ${getColor(vars.colors.danger)};
              `}
            >
              {salePriceStr}
            </span>
          )}
          <span
            css={css`
              font-size: ${hasSale ? '12px' : '18px'};
              font-weight: 500;
              color: ${hasSale ? getColor(vars.colors.content2) : getColor(vars.colors.content)};
              text-decoration: ${hasSale ? 'line-through' : 'none'};
            `}
          >
            {isRecurring ? `${priceStr}/mo` : priceStr}
          </span>
        </div>
      )}
    </div>
  );
}

type BumpOfferCardProps = {
  bumpProduct: string;
  bumpOfferTitle: string;
  origPrice: string;
};

function BumpOfferCard({ bumpProduct, bumpOfferTitle, origPrice }: BumpOfferCardProps) {
  let origPriceStr = '$19.99';
  if (origPrice) {
    origPriceStr = origPrice.startsWith('$') ? origPrice : `$${origPrice}`;
  }
  return (
    <div
      css={css`
        background: white;
        border-radius: 10px;
        position: relative;
        border: 1px solid ${getColor(vars.colors.border)};
        box-shadow:
          0 2px 3px rgba(0, 0, 0, 0.05),
          0 1px 1px rgba(0, 0, 0, 0.05);
        display: flex;
        gap: 24px;
        padding: 24px;
        align-items: flex-start;
      `}
    >
      {/* Image */}
      <div
        css={css`
          width: 200px;
          height: 200px;
          border-radius: 4px;
          overflow: hidden;
          flex-shrink: 0;
          background: ${getColor(vars.colors.background3)};
        `}
      >
        <img
          src={wandPatternImg}
          alt=""
          css={css`
            width: 100%;
            height: 100%;
            object-fit: cover;
          `}
        />
      </div>

      {/* Badge */}
      <div
        css={css`
          position: absolute;
          top: 27px;
          left: 27px;
          padding: 0 12px;
          height: 26px;
          display: flex;
          align-items: center;
          justify-content: center;
          border-radius: 4px;
          font-size: 12px;
          font-weight: 500;
          background: white;
          border: 1px solid ${getColor(vars.colors.border)};
          color: ${getColor(vars.colors.content)};
        `}
      >
        {bumpOfferTitle || 'Bonus offer'}
      </div>

      {/* Content */}
      <div
        css={css`
          display: flex;
          flex-direction: column;
          justify-content: space-between;
          width: 252px;
          height: 200px;
        `}
      >
        <div>
          <p
            css={css`
              font-size: 16px;
              font-weight: 500;
              line-height: 24px;
              color: ${getColor(vars.colors.content)};
              margin-bottom: 8px;
            `}
          >
            {bumpProduct || 'Wand Pattern Pack - 24 TOTAL'}
          </p>
          <div
            css={css`
              font-size: 14px;
              line-height: 20px;
              color: ${getColor(vars.colors.content)};
            `}
          >
            <p>
              The perfect project to use your leftover felt scraps and create another heartfelt gift
              for your little one :) Matching patterns for the Birthday Crown themes (2x per...
            </p>
            <p
              css={css`
                text-decoration: underline;
                color: rgba(57, 48, 45, 0.52);
                line-height: 20px;
              `}
            >
              See more
            </p>
          </div>
        </div>

        {/* Price row + button */}
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: space-between;
            width: 100%;
          `}
        >
          <span
            css={css`
              font-size: 0;
              line-height: 0;
            `}
          >
            <span
              css={css`
                font-size: 18px;
                font-weight: 500;
                color: #d33003;
                line-height: 23.2px;
              `}
            >
              $12.00
            </span>{' '}
            <span
              css={css`
                font-size: 12px;
                text-decoration: line-through;
                line-height: 17.8px;
                color: ${getColor(vars.colors.content)};
              `}
            >
              {origPriceStr}
            </span>
          </span>
          <div
            css={css`
              height: 40px;
              padding: 0 17px;
              border-radius: 6px;
              border: 1px solid ${getColor(vars.colors.border)};
              background: white;
              display: flex;
              align-items: center;
              justify-content: center;
              gap: 12px;
              font-size: 14px;
              font-weight: 500;
              color: ${getColor(vars.colors.content)};
              cursor: pointer;
            `}
          >
            Add to order
          </div>
        </div>
      </div>
    </div>
  );
}

type ConfirmCardProps = { title: string; desc: string; btnText: string };

function ConfirmCard({ title, desc, btnText }: ConfirmCardProps) {
  return (
    <div
      css={css`
        background: white;
        border-radius: 16px;
        padding: 40px;
        width: 420px;
        text-align: center;
        border: 1px solid ${getColor(vars.colors.border)};
      `}
    >
      <h3
        css={css`
          font-size: 24px;
          font-weight: 500;
          margin-bottom: 16px;
          color: ${getColor(vars.colors.content)};
        `}
      >
        {title || 'Order confirmation'}
      </h3>
      <p
        css={css`
          font-size: 14px;
          line-height: 24px;
          margin-bottom: 32px;
          color: ${getColor(vars.colors.content)};
        `}
      >
        {desc || 'Thank you for your purchase!'}
      </p>
      <div
        css={css`
          display: inline-flex;
          align-items: center;
          justify-content: center;
          height: 48px;
          padding: 0 40px;
          border-radius: 10px;
          font-size: 14px;
          font-weight: 500;
          color: white;
          background: ${getColor(vars.colors.content)};
        `}
      >
        {btnText || 'Go to my page'}
      </div>
    </div>
  );
}

type TestimonialCardProps = { name: string; text: string; image: string | null };

function TestimonialCard({ name, text, image }: TestimonialCardProps) {
  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        text-align: center;
        width: 480px;
      `}
    >
      <div
        css={css`
          width: 106px;
          height: 106px;
          border-radius: 50%;
          overflow: hidden;
          margin-bottom: 24px;
          background: ${getColor(vars.colors.background3)};
          flex-shrink: 0;
        `}
      >
        {image && (
          <img
            src={image}
            alt=""
            css={css`
              width: 100%;
              height: 100%;
              object-fit: cover;
            `}
          />
        )}
      </div>
      <p
        css={css`
          font-family: Georgia, serif;
          font-size: 21px;
          line-height: 27px;
          margin-bottom: 16px;
          color: ${getColor(vars.colors.content)};
          width: 395px;
        `}
      >
        {text ? `\u201C${text}\u201D` : ''}
      </p>
      <p
        css={css`
          font-size: 18.6px;
          color: ${getColor(vars.colors.content)};
        `}
      >
        {name}
      </p>
    </div>
  );
}

// ─── Product select modal ─────────────────────────────────────────────────────

type ProductSelectModalProps = {
  onSelect: (name: string) => void;
  onCancel: () => void;
};

function ProductSelectModal({ onSelect, onCancel }: ProductSelectModalProps) {
  const [selected, setSelected] = useState(PRODUCTS[0].name);

  return (
    <div
      css={css`
        position: fixed;
        inset: 0;
        z-index: 1000;
        background: rgba(0, 0, 0, 0.4);
        display: flex;
        align-items: center;
        justify-content: center;
      `}
      role="button"
      tabIndex={0}
      onClick={onCancel}
      onKeyDown={e => {
        if (e.key === 'Escape') onCancel();
      }}
    >
      {/* eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions */}
      <div
        css={css`
          width: 940px;
          height: 720px;
          background: white;
          border-radius: 10px;
          box-shadow:
            0 23px 86px -14px rgba(0, 0, 0, 0.19),
            0 6px 14px -2px rgba(0, 0, 0, 0.09),
            0 2px 3px rgba(0, 0, 0, 0.05),
            0 1px 1px rgba(0, 0, 0, 0.05);
          display: flex;
          flex-direction: column;
          overflow: hidden;
        `}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          css={css`
            padding: 40px 56px 24px;
          `}
        >
          <p
            css={css`
              font-size: 18px;
              font-weight: 500;
              line-height: 23.2px;
              color: ${getColor(vars.colors.content)};
              margin-bottom: 4px;
            `}
          >
            Select product
          </p>
          <p
            css={css`
              font-size: 14px;
              line-height: 20px;
              color: ${getColor(vars.colors.content2)};
            `}
          >
            Products selected here will be shown as upsells on your checkout
          </p>
        </div>

        {/* Table */}
        <div
          css={css`
            flex: 1;
            overflow-y: auto;
            padding: 0 56px;
          `}
        >
          {/* Table header */}
          <div
            css={css`
              display: flex;
              align-items: center;
              height: 36px;
            `}
          >
            <div
              css={css`
                width: 64px;
                flex-shrink: 0;
              `}
            />
            <div
              css={css`
                width: 508px;
                flex-shrink: 0;
                font-size: 14px;
                font-weight: 500;
                color: ${getColor(vars.colors.content)};
              `}
            >
              Product
            </div>
            <div
              css={css`
                width: 120px;
                flex-shrink: 0;
                font-size: 14px;
                font-weight: 500;
                color: ${getColor(vars.colors.content)};
                padding-left: 8px;
              `}
            >
              Price
            </div>
            <div
              css={css`
                width: 100px;
                flex-shrink: 0;
                font-size: 14px;
                font-weight: 500;
                color: ${getColor(vars.colors.content)};
                padding-left: 8px;
              `}
            >
              Stock
            </div>
            <div
              css={css`
                width: 100px;
                flex-shrink: 0;
                font-size: 14px;
                font-weight: 500;
                color: ${getColor(vars.colors.content)};
                padding-left: 8px;
              `}
            >
              Sold
            </div>
            <div
              css={css`
                width: 36px;
                flex-shrink: 0;
              `}
            />
          </div>

          {/* Table rows */}
          {PRODUCTS.map(product => (
            <button
              key={product.name}
              type="button"
              onClick={() => setSelected(product.name)}
              css={css`
                display: flex;
                align-items: center;
                width: 100%;
                height: 100px;
                border: none;
                border-bottom: 1px solid #f3f2f1;
                background: none;
                cursor: pointer;
                padding: 0;
                font-family: inherit;
                text-align: left;

                &:hover {
                  background: #fafaf8;
                }
              `}
            >
              {/* Radio */}
              <div
                css={css`
                  width: 64px;
                  flex-shrink: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                `}
              >
                <div
                  css={css`
                    width: 16px;
                    height: 16px;
                    border-radius: 50%;
                    ${selected === product.name
                      ? `background: ${getColor(vars.colors.content)};
                         box-shadow: inset 0 0 0 4px white;`
                      : `border: 1px solid rgba(58, 58, 44, 0.31);`}
                  `}
                />
              </div>

              {/* Thumbnail + name */}
              <div
                css={css`
                  width: 508px;
                  flex-shrink: 0;
                  display: flex;
                  align-items: center;
                  gap: 16px;
                  padding-right: 40px;
                `}
              >
                <img
                  src={product.thumb}
                  alt=""
                  css={css`
                    width: 60px;
                    height: 60px;
                    border-radius: 4px;
                    object-fit: cover;
                    flex-shrink: 0;
                  `}
                />
                <p
                  css={css`
                    font-size: 14px;
                    color: #000;
                    overflow: hidden;
                    text-overflow: ellipsis;
                    white-space: nowrap;
                  `}
                >
                  {product.name}
                </p>
              </div>

              {/* Price */}
              <div
                css={css`
                  width: 120px;
                  flex-shrink: 0;
                  font-size: 14px;
                  color: ${getColor(vars.colors.content)};
                  padding-left: 8px;
                `}
              >
                {product.price}
              </div>

              {/* Stock */}
              <div
                css={css`
                  width: 100px;
                  flex-shrink: 0;
                  font-size: 14px;
                  color: ${getColor(vars.colors.content)};
                  padding-left: 8px;
                `}
              >
                {product.stock}
              </div>

              {/* Sold */}
              <div
                css={css`
                  width: 100px;
                  flex-shrink: 0;
                  font-size: 14px;
                  color: ${getColor(vars.colors.content)};
                  padding-left: 8px;
                `}
              >
                {product.sold}
              </div>

              {/* More */}
              <div
                css={css`
                  width: 36px;
                  flex-shrink: 0;
                  display: flex;
                  align-items: center;
                  justify-content: center;
                  font-size: 18px;
                  color: ${getColor(vars.colors.content)};
                  letter-spacing: 2px;
                `}
              >
                &middot;&middot;&middot;
              </div>
            </button>
          ))}
        </div>

        {/* Footer */}
        <div
          css={css`
            border-top: 1px solid ${getColor(vars.colors.border)};
            padding: 24px 40px;
            display: flex;
            justify-content: flex-end;
            gap: 8px;
          `}
        >
          <button
            type="button"
            onClick={onCancel}
            css={css`
              height: 40px;
              padding: 0 17px;
              border-radius: 6px;
              border: 1px solid rgba(58, 58, 44, 0.31);
              background: white;
              font-size: 14px;
              font-weight: 500;
              color: ${getColor(vars.colors.content)};
              cursor: pointer;
              font-family: inherit;
            `}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={() => onSelect(selected)}
            css={css`
              height: 40px;
              padding: 0 17px;
              border-radius: 6px;
              border: 1px solid transparent;
              background: ${getColor(vars.colors.content)};
              font-size: 14px;
              font-weight: 500;
              color: #f9f9f6;
              cursor: pointer;
              font-family: inherit;
            `}
          >
            Select
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Right panel ──────────────────────────────────────────────────────────────

type PreviewPanelProps = {
  state: PreviewState;
  productName: string;
  productDesc: string;
  productImage: string | null;
  price: string;
  salePrice: string;
  onSale: boolean;
  isRecurring: boolean;
  bumpProduct: string;
  bumpOfferTitle: string;
  confirmTitle: string;
  confirmDesc: string;
  confirmBtnText: string;
  testimonialName: string;
  testimonialText: string;
  testimonialImage: string | null;
};

function PreviewPanel(props: PreviewPanelProps) {
  const {
    state,
    productName,
    productDesc,
    productImage,
    price,
    salePrice,
    onSale,
    isRecurring,
    bumpProduct,
    bumpOfferTitle,
    confirmTitle,
    confirmDesc,
    confirmBtnText,
    testimonialName,
    testimonialText,
    testimonialImage,
  } = props;

  const cardKey = state >= 1 && state <= 4 ? 'product' : String(state);

  return (
    <div
      css={css`
        flex: 1;
        display: flex;
        align-items: center;
        justify-content: center;
        overflow: hidden;
        position: relative;
      `}
    >
      <div
        css={css`
          position: relative;
          width: 628px;
          height: 826px;
          border-radius: 16px;
          overflow: hidden;
          background: ${getColor(vars.colors.background2)};
        `}
      >
        <AnimatePresence mode="popLayout" initial={false}>
          <motion.div
            key={cardKey}
            initial={{ opacity: 0, y: '100%', rotate: 4 }}
            animate={{ opacity: 1, y: 0, rotate: 0 }}
            exit={{ opacity: 0, y: '-100%', rotate: -4 }}
            transition={{ type: 'spring', stiffness: 80, damping: 20, mass: 1, duration: 0.6 }}
            css={css`
              position: absolute;
              inset: 0;
              display: flex;
              align-items: center;
              justify-content: center;
            `}
          >
            {state === 0 && <HeroCard />}
            {state >= 1 && state <= 4 && (
              <ProductCard
                name={productName}
                desc={productDesc}
                image={productImage}
                price={price}
                salePrice={salePrice}
                onSale={onSale}
                isRecurring={isRecurring}
              />
            )}
            {state === 5 && (
              <BumpOfferCard
                bumpProduct={bumpProduct}
                bumpOfferTitle={bumpOfferTitle}
                origPrice={price}
              />
            )}
            {state === 6 && (
              <ConfirmCard title={confirmTitle} desc={confirmDesc} btnText={confirmBtnText} />
            )}
            {state === 7 && (
              <TestimonialCard
                name={testimonialName}
                text={testimonialText}
                image={testimonialImage}
              />
            )}
          </motion.div>
        </AnimatePresence>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

export default function ProductCreationForm() {
  const [productName, setProductName] = useState('');
  const [productDesc, setProductDesc] = useState('');
  const [productImage, setProductImage] = useState<string | null>(null);

  const [isRecurring, setIsRecurring] = useState(false);
  const [priceInput, setPriceInput] = useState('');
  const [onSaleActive, setOnSaleActive] = useState(false);
  const [salePriceInput, setSalePriceInput] = useState('');
  const [paymentPlanOpen, setPaymentPlanOpen] = useState(false);
  const [salesTaxOpen, setSalesTaxOpen] = useState(false);
  const [salesTaxOpenRecurring, setSalesTaxOpenRecurring] = useState(false);
  const [recurringPrice, setRecurringPrice] = useState('$39');

  const [physicalProduct, setPhysicalProduct] = useState(false);
  const [limitQtyOpen, setLimitQtyOpen] = useState(false);
  const [endDateOpen, setEndDateOpen] = useState(false);

  const [bumpProduct, setBumpProduct] = useState('');
  const [bumpOfferTitle, setBumpOfferTitle] = useState('');
  const [bumpSelected, setBumpSelected] = useState(false);

  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmDesc, setConfirmDesc] = useState('');
  const [confirmBtnText, setConfirmBtnText] = useState('');

  const [testimonialEnabled, setTestimonialEnabled] = useState(false);
  const [testimonialName, setTestimonialName] = useState('');
  const [testimonialText, setTestimonialText] = useState('');
  const [testimonialImage, setTestimonialImage] = useState<string | null>(null);
  const [bumpEnabled, setBumpEnabled] = useState(false);
  const [showProductModal, setShowProductModal] = useState(false);
  const [segmentSelected, setSegmentSelected] = useState(false);

  const [activeSection, setActiveSection] = useState<
    'product' | 'pricing' | 'bump' | 'confirm' | 'testimonial' | null
  >(null);

  // Restore form state only when returning from preview, then clear the draft
  useEffect(() => {
    const saved = localStorage.getItem('formDraft');
    if (!saved) return;
    localStorage.removeItem('formDraft');
    try {
      const d = JSON.parse(saved);
      if (d.productName) setProductName(d.productName);
      if (d.productDesc) setProductDesc(d.productDesc);
      if (d.productImage) setProductImage(d.productImage);
      if (d.isRecurring) setIsRecurring(d.isRecurring);
      if (d.priceInput) setPriceInput(d.priceInput);
      if (d.onSaleActive) setOnSaleActive(d.onSaleActive);
      if (d.salePriceInput) setSalePriceInput(d.salePriceInput);
      if (d.recurringPrice) setRecurringPrice(d.recurringPrice);
      if (d.bumpProduct) setBumpProduct(d.bumpProduct);
      if (d.bumpOfferTitle) setBumpOfferTitle(d.bumpOfferTitle);
      if (d.bumpSelected) setBumpSelected(d.bumpSelected);
      if (d.bumpEnabled) setBumpEnabled(d.bumpEnabled);
      if (d.confirmTitle) setConfirmTitle(d.confirmTitle);
      if (d.confirmDesc) setConfirmDesc(d.confirmDesc);
      if (d.confirmBtnText) setConfirmBtnText(d.confirmBtnText);
      if (d.testimonialEnabled) setTestimonialEnabled(d.testimonialEnabled);
      if (d.testimonialName) setTestimonialName(d.testimonialName);
      if (d.testimonialText) setTestimonialText(d.testimonialText);
      if (d.testimonialImage) setTestimonialImage(d.testimonialImage);
    } catch (_e) {
      /* ignore */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const imageUploadRef = useRef<HTMLInputElement>(null);
  const testimonialUploadRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();

  function handlePreview() {
    // Save preview data for the preview page
    localStorage.setItem(
      'previewData',
      JSON.stringify({
        productName,
        productDesc,
        productImage,
        price: priceInput,
        salePrice: salePriceInput,
        onSaleActive,
        isRecurring,
        recurringPrice,
        bumpProduct,
        bumpOfferTitle,
        testimonialName,
        testimonialText,
        testimonialImage,
        confirmTitle,
        confirmDesc,
        confirmBtnText,
      }),
    );
    // Save form draft so data persists when returning from preview
    localStorage.setItem(
      'formDraft',
      JSON.stringify({
        productName,
        productDesc,
        productImage,
        isRecurring,
        priceInput,
        onSaleActive,
        salePriceInput,
        recurringPrice,
        bumpProduct,
        bumpOfferTitle,
        bumpSelected,
        bumpEnabled,
        confirmTitle,
        confirmDesc,
        confirmBtnText,
        testimonialEnabled,
        testimonialName,
        testimonialText,
        testimonialImage,
      }),
    );
    navigate('/preview');
  }

  function getPreviewState(): PreviewState {
    const hasTestimonial = testimonialEnabled && (testimonialName.trim() || testimonialText.trim());
    const hasConfirm = confirmTitle.trim() || confirmDesc.trim();
    const hasBump = bumpEnabled && bumpProduct.trim();
    const hasPrice = isRecurring ? recurringPrice.trim() : priceInput.trim();

    // Show the card for whichever section the user is currently editing
    if (activeSection === 'testimonial' && hasTestimonial) return 7;
    if (activeSection === 'confirm' && hasConfirm) return 6;
    if (activeSection === 'bump' && hasBump) return 5;
    if (activeSection === 'pricing') {
      if (onSaleActive && hasPrice && salePriceInput.trim()) return 4;
      if (hasPrice) return 3;
    }
    if (activeSection === 'product') {
      if (productImage && (productName.trim() || productDesc.trim())) return 2;
      if (productName.trim() && productDesc.trim()) return 1;
    }

    // Fall back to priority order when no section is active
    if (hasConfirm) return 6;
    if (hasBump) return 5;
    if (hasTestimonial) return 7;
    if (onSaleActive && hasPrice && salePriceInput.trim()) return 4;
    if (hasPrice) return 3;
    if (productImage && (productName.trim() || productDesc.trim())) return 2;
    if (productName.trim() && productDesc.trim()) return 1;
    return 0;
  }

  const previewState = getPreviewState();

  const spring = { type: 'spring', stiffness: 300, damping: 30 } as const;
  const collapseAnim = {
    initial: { opacity: 0, height: 0 },
    animate: { opacity: 1, height: 'auto' },
    exit: { opacity: 0, height: 0 },
    transition: spring,
    css: css`
      overflow: hidden;
    `,
  };

  return (
    <div
      css={css`
        height: 100vh;
        overflow: hidden;
        background: ${getColor(vars.colors.background2)};
        color: ${getColor(vars.colors.content)};
        font-family:
          'Flodesk Sans',
          -apple-system,
          BlinkMacSystemFont,
          'Segoe UI',
          system-ui,
          sans-serif;
        * {
          box-sizing: border-box;
          margin: 0;
          padding: 0;
        }
        input,
        textarea,
        select,
        button {
          font-family: inherit;
        }
      `}
    >
      {/* ── Top nav ── */}
      <nav
        css={css`
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          height: 64px;
          background: white;
          z-index: 50;
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 0 20px;
          border-bottom: 1px solid ${getColor(vars.colors.border)};
        `}
      >
        <Button variant="neutral">Cancel</Button>
        <div
          css={css`
            display: flex;
            align-items: center;
            gap: 16px;
          `}
        >
          <Button
            variant="neutral"
            icon={<img src={DevicesIcon} alt="" width={18} height={18} />}
            iconPosition="left"
            onClick={() => handlePreview()}
          >
            Preview
          </Button>
          <Button variant="accent">Create product</Button>
        </div>
      </nav>

      {/* ── Main layout ── */}
      <div
        css={css`
          display: flex;
          padding-top: 64px;
          height: 100vh;
        `}
      >
        {/* ── Left panel ── */}
        <div
          css={css`
            width: 494px;
            flex-shrink: 0;
            overflow-y: auto;
            height: 100%;
            padding-top: 50px;
            margin-left: 148px;
            scrollbar-width: none;
            &::-webkit-scrollbar {
              display: none;
            }
          `}
        >
          {/* Product Details */}
          <section css={sectionStyle} onFocusCapture={() => setActiveSection('product')}>
            <h2 css={sectionTitleStyle}>Product details</h2>

            <div
              css={css`
                margin-bottom: 24px;
              `}
            >
              <TextInput
                label="Product name"
                placeholder="How to cook e-book"
                value={productName}
                onChange={e => setProductName(e.target.value)}
              />
            </div>

            <div
              css={css`
                margin-bottom: 24px;
                textarea {
                  padding: 12px 18px;
                }
              `}
            >
              <Textarea
                label="Description"
                placeholder="Product description"
                value={productDesc}
                onChange={e => setProductDesc(e.target.value)}
                rows={5}
              />
            </div>

            <div>
              <p
                css={css`
                  font-size: 14px;
                  font-weight: 500;
                  margin-bottom: 8px;
                  color: ${getColor(vars.colors.content)};
                `}
              >
                Add image from
              </p>
              <div
                css={css`
                  margin-bottom: 8px;
                `}
              >
                <Select options={imageSourceOptions} value="computer" onChange={() => {}} />
              </div>
              <ImageUpload
                image={productImage}
                height={200}
                onUpload={url => setProductImage(url)}
                inputRef={imageUploadRef}
              />
            </div>
          </section>

          {/* Pricing */}
          <section
            onFocusCapture={() => setActiveSection('pricing')}
            css={css`
              ${sectionStyle.styles};
              margin-top: 12px;
            `}
          >
            <h2 css={sectionTitleStyle}>Pricing</h2>

            {/* Payment type toggle */}
            <div
              css={css`
                margin-bottom: 24px;
              `}
            >
              <TextToggleGroup hasFullWidth>
                <TextToggle isActive={!isRecurring} onClick={() => setIsRecurring(false)}>
                  One-time payment
                </TextToggle>
                <TextToggle isActive={isRecurring} onClick={() => setIsRecurring(true)}>
                  Recurring payment
                </TextToggle>
              </TextToggleGroup>
            </div>

            {/* One-time fields */}
            <AnimatePresence initial={false}>
              {!isRecurring && (
                <motion.div {...collapseAnim}>
                  <div
                    css={css`
                      margin-bottom: 24px;
                    `}
                  >
                    <TextInputWithSuffix
                      label="Price"
                      value={priceInput}
                      onChange={e => setPriceInput(e.target.value)}
                      placeholder="$9.99"
                      suffix="USD"
                    />
                  </div>

                  <hr css={separatorStyle} />
                  <div
                    css={css`
                      ${rowBetweenStyle.styles};
                      margin-bottom: 24px;
                    `}
                  >
                    <span
                      css={css`
                        font-size: 14px;
                        font-weight: 500;
                        color: ${getColor(vars.colors.content)};
                      `}
                    >
                      On sale
                    </span>
                    <Switch
                      id="switch-onSale"
                      isActive={onSaleActive}
                      onChange={() => setOnSaleActive(v => !v)}
                    />
                  </div>

                  <AnimatePresence initial={false}>
                    {onSaleActive && (
                      <motion.div
                        {...collapseAnim}
                        css={css`
                          overflow: hidden;
                          margin-bottom: 24px;
                        `}
                      >
                        <TextInputWithSuffix
                          label="Sale price"
                          value={salePriceInput}
                          onChange={e => setSalePriceInput(e.target.value)}
                          placeholder="9.99"
                          suffix="USD"
                        />
                      </motion.div>
                    )}
                  </AnimatePresence>

                  <hr css={separatorStyle} />
                  <div
                    css={css`
                      margin-bottom: 24px;
                    `}
                  >
                    <div css={rowBetweenStyle}>
                      <span
                        css={css`
                          font-size: 14px;
                          font-weight: 500;
                          color: ${getColor(vars.colors.content)};
                        `}
                      >
                        Allow payment plan
                      </span>
                      <Switch
                        id="switch-paymentPlan"
                        isActive={paymentPlanOpen}
                        onChange={() => setPaymentPlanOpen(v => !v)}
                      />
                    </div>
                    <AnimatePresence initial={false}>
                      {paymentPlanOpen && (
                        <motion.div {...collapseAnim}>
                          <div
                            css={css`
                              margin-top: 16px;
                              display: flex;
                              flex-direction: column;
                              gap: 16px;
                            `}
                          >
                            <TextInputWithSuffix
                              label="Total price"
                              defaultValue="$100"
                              placeholder="$100"
                              suffix="USD"
                            />
                            <div
                              css={css`
                                display: flex;
                                gap: 8px;
                              `}
                            >
                              <div
                                css={css`
                                  flex: 1;
                                `}
                              >
                                <TextInput label="Installments" defaultValue="4" />
                              </div>
                              <div
                                css={css`
                                  flex: 1;
                                `}
                              >
                                <Select
                                  label="Frequency"
                                  options={[
                                    { value: 'monthly', content: 'Monthly' },
                                    { value: 'weekly', content: 'Weekly' },
                                    { value: 'biweekly', content: 'Bi-weekly' },
                                  ]}
                                  defaultValue="monthly"
                                />
                              </div>
                            </div>
                            <div
                              css={css`
                                display: flex;
                                align-items: center;
                                justify-content: center;
                                padding: 12px 16px;
                                border-radius: 10px;
                                font-size: 14px;
                                text-align: center;
                                background: ${getColor(vars.colors.background3)};
                                color: ${getColor(vars.colors.content)};
                              `}
                            >
                              Buyer will pay $50 per month for 4 months - $100 total.
                            </div>
                            <p
                              css={css`
                                font-size: 12px;
                                text-align: center;
                                color: ${getColor(vars.colors.content2)};
                              `}
                            >
                              Remember that you take the risk if the customer doesn&apos;t pay.{' '}
                              <button
                                type="button"
                                css={css`
                                  background: none;
                                  border: none;
                                  padding: 0;
                                  cursor: pointer;
                                  text-decoration: underline;
                                  color: inherit;
                                  font: inherit;
                                `}
                              >
                                Learn more
                              </button>
                            </p>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>

                  <hr css={separatorStyle} />
                  <div>
                    <div css={rowBetweenStyle}>
                      <span
                        css={css`
                          font-size: 14px;
                          font-weight: 500;
                          color: ${getColor(vars.colors.content)};
                        `}
                      >
                        Enable sales tax
                      </span>
                      <Switch
                        id="switch-salesTax"
                        isActive={salesTaxOpen}
                        onChange={() => setSalesTaxOpen(v => !v)}
                      />
                    </div>
                    <AnimatePresence initial={false}>
                      {salesTaxOpen && (
                        <motion.div {...collapseAnim}>
                          <div
                            css={css`
                              margin-top: 16px;
                            `}
                          >
                            <p
                              css={css`
                                font-size: 14px;
                                margin-bottom: 8px;
                                color: ${getColor(vars.colors.content)};
                              `}
                            >
                              Tax category
                            </p>
                            <div
                              css={css`
                                display: flex;
                                align-items: center;
                                justify-content: space-between;
                                width: 100%;
                                height: 40px;
                                padding: 0 12px;
                                border-radius: 8px;
                                border: 1px solid ${getColor(vars.colors.border)};
                                background: ${getColor(vars.colors.background2)};
                              `}
                            >
                              <span
                                css={css`
                                  font-size: 14px;
                                  color: ${getColor(vars.colors.content2)};
                                `}
                              >
                                General - Services
                              </span>
                              <button
                                type="button"
                                css={css`
                                  background: none;
                                  border: none;
                                  padding: 0;
                                  cursor: pointer;
                                  font-size: 14px;
                                  font-weight: 500;
                                  color: ${getColor(vars.colors.content)};
                                  text-decoration: none;
                                `}
                              >
                                Edit
                              </button>
                            </div>
                          </div>
                        </motion.div>
                      )}
                    </AnimatePresence>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            {/* Recurring fields */}
            <AnimatePresence initial={false}>
              {isRecurring && (
                <motion.div {...collapseAnim}>
                  <div
                    css={css`
                      margin-bottom: 16px;
                    `}
                  >
                    <div
                      css={css`
                        display: flex;
                        align-items: center;
                        gap: 8px;
                        width: 100%;
                        height: 48px;
                        padding: 0 16px;
                        border-radius: 10px;
                        font-size: 14px;
                        font-weight: 500;
                        background: ${getColor(vars.colors.background3)};
                        color: ${getColor(vars.colors.content)};
                      `}
                    >
                      <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
                        <circle cx="1" cy="1" r="1" fill="#b9b9ac" />
                        <circle cx="5" cy="1" r="1" fill="#b9b9ac" />
                        <circle cx="1" cy="5" r="1" fill="#b9b9ac" />
                        <circle cx="5" cy="5" r="1" fill="#b9b9ac" />
                        <circle cx="1" cy="9" r="1" fill="#b9b9ac" />
                        <circle cx="5" cy="9" r="1" fill="#b9b9ac" />
                      </svg>
                      {recurringPrice || '$39'} Monthly
                    </div>
                  </div>

                  <div
                    css={css`
                      margin-bottom: 16px;
                      padding: 16px;
                      border-radius: 10px;
                      background: ${getColor(vars.colors.background3)};
                    `}
                  >
                    <p
                      css={css`
                        font-size: 14px;
                        font-weight: 500;
                        margin-bottom: 12px;
                        color: ${getColor(vars.colors.content)};
                      `}
                    >
                      Edit subscription
                    </p>
                    <div
                      css={css`
                        margin-bottom: 12px;
                      `}
                    >
                      <TextInputWithSuffix
                        value={recurringPrice}
                        onChange={e => setRecurringPrice(e.target.value)}
                        placeholder="$39"
                        suffix="USD"
                      />
                    </div>
                    <Select
                      options={[
                        { value: 'annually', content: 'Annually' },
                        { value: 'monthly', content: 'Monthly' },
                        { value: 'weekly', content: 'Weekly' },
                      ]}
                      defaultValue="annually"
                    />
                  </div>

                  <div
                    css={css`
                      display: flex;
                      justify-content: flex-end;
                      margin-bottom: 24px;
                    `}
                  >
                    <Button variant="neutral">Add another</Button>
                  </div>

                  <hr css={separatorStyle} />
                  <div css={rowBetweenStyle}>
                    <span
                      css={css`
                        font-size: 14px;
                        font-weight: 500;
                        color: ${getColor(vars.colors.content)};
                      `}
                    >
                      Enable sales tax
                    </span>
                    <Switch
                      id="switch-salesTaxRecurring"
                      isActive={salesTaxOpenRecurring}
                      onChange={() => setSalesTaxOpenRecurring(v => !v)}
                    />
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </section>

          {/* Additional Information */}
          <section
            css={css`
              ${sectionStyle.styles};
              margin-top: 12px;
            `}
          >
            <h2 css={sectionTitleStyle}>Additional information</h2>

            <div
              css={css`
                margin-bottom: 8px;
              `}
            >
              <p
                css={css`
                  font-size: 14px;
                  font-weight: 500;
                  color: ${getColor(vars.colors.content)};
                `}
              >
                Product segment
              </p>
              <p
                css={css`
                  font-size: 14px;
                  color: ${getColor(vars.colors.content2)};
                `}
              >
                Add customers to a segment after purchase
              </p>
            </div>
            <div
              css={css`
                margin-bottom: 24px;
              `}
            >
              {!segmentSelected ? (
                <button
                  type="button"
                  onClick={() => setSegmentSelected(true)}
                  css={css`
                    width: 100%;
                    height: 40px;
                    padding: 0 13px;
                    border-radius: 6px;
                    border: 1px solid rgba(61, 46, 41, 0.24);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                    font-size: 14px;
                    cursor: pointer;
                    background: none;
                    font-family: inherit;
                  `}
                >
                  <span
                    css={css`
                      color: ${getColor(vars.colors.content2)};
                    `}
                  >
                    Find or create a segment
                  </span>
                  <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                    <path
                      d="M1 7L5 11L9 7"
                      stroke="#10100f"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </button>
              ) : (
                <div
                  css={css`
                    width: 100%;
                    height: 40px;
                    padding: 0 12px;
                    border-radius: 6px;
                    border: 1px solid rgba(61, 46, 41, 0.24);
                    display: flex;
                    align-items: center;
                    justify-content: space-between;
                  `}
                >
                  <div
                    css={css`
                      display: flex;
                      align-items: center;
                      height: 28px;
                      background: #e9c0fd;
                      border-radius: 3px;
                    `}
                  >
                    <span
                      css={css`
                        padding-left: 8px;
                        font-size: 12px;
                        font-weight: 500;
                        color: ${getColor(vars.colors.content)};
                        white-space: nowrap;
                        overflow: hidden;
                        text-overflow: ellipsis;
                      `}
                    >
                      Buyers2026
                    </span>
                    <button
                      type="button"
                      onClick={() => setSegmentSelected(false)}
                      css={css`
                        display: flex;
                        align-items: center;
                        justify-content: center;
                        padding: 0 8px;
                        height: 100%;
                        background: none;
                        border: none;
                        cursor: pointer;
                        font-size: 14px;
                        color: ${getColor(vars.colors.content)};
                        font-family: inherit;
                        line-height: 1;
                      `}
                    >
                      &times;
                    </button>
                  </div>
                  <svg width="6" height="10" viewBox="0 0 6 10" fill="none">
                    <path
                      d="M1 1L5 5L1 9"
                      stroke="#10100f"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              )}
            </div>

            <hr css={separatorStyle} />
            <div
              css={css`
                margin-bottom: 24px;
              `}
            >
              <ToggleRow
                id="switch-physical"
                label="Physical product"
                hint="Add shipping fields to your payment page"
                active={physicalProduct}
                onChange={setPhysicalProduct}
              />
            </div>

            <hr css={separatorStyle} />
            <div
              css={css`
                margin-bottom: 24px;
              `}
            >
              <ToggleRow
                id="switch-limitQty"
                label="Limit quantity"
                hint="Limit the amount the can be sold"
                active={limitQtyOpen}
                onChange={setLimitQtyOpen}
              />
              <AnimatePresence initial={false}>
                {limitQtyOpen && (
                  <motion.div {...collapseAnim}>
                    <div
                      css={css`
                        margin-top: 16px;
                      `}
                    >
                      <TextInput type="number" defaultValue="20" />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <hr css={separatorStyle} />
            <div>
              <ToggleRow
                id="switch-endDate"
                label="Set an end date"
                hint="Define when your offer should end"
                active={endDateOpen}
                onChange={setEndDateOpen}
              />
              <AnimatePresence initial={false}>
                {endDateOpen && (
                  <motion.div {...collapseAnim}>
                    <div
                      css={css`
                        margin-top: 16px;
                      `}
                    >
                      <TextInput
                        defaultValue="March 27, 2026 12:10 PM CET"
                        icon={
                          <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
                            <rect
                              x="0.5"
                              y="1.5"
                              width="15"
                              height="15"
                              rx="1.5"
                              stroke="#10100f"
                              strokeWidth="1"
                            />
                            <line
                              x1="0.5"
                              y1="5.5"
                              x2="15.5"
                              y2="5.5"
                              stroke="#10100f"
                              strokeWidth="1"
                            />
                            <line
                              x1="4"
                              y1="0"
                              x2="4"
                              y2="3"
                              stroke="#10100f"
                              strokeWidth="1"
                              strokeLinecap="round"
                            />
                            <line
                              x1="12"
                              y1="0"
                              x2="12"
                              y2="3"
                              stroke="#10100f"
                              strokeWidth="1"
                              strokeLinecap="round"
                            />
                          </svg>
                        }
                      />
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </section>

          {/* Selling tools */}
          <section
            css={css`
              ${sectionStyle.styles};
              margin-top: 12px;
            `}
          >
            <h2 css={sectionTitleStyle}>Selling tools</h2>

            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 24px;
              `}
            >
              {/* Testimonial toggle */}
              <div onFocusCapture={() => setActiveSection('testimonial')}>
                <div
                  css={css`
                    display: flex;
                    gap: 16px;
                    align-items: flex-start;
                  `}
                >
                  <div
                    css={css`
                      flex: 1;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 14px;
                        font-weight: 500;
                        color: ${getColor(vars.colors.content)};
                      `}
                    >
                      Testimonial
                    </p>
                    <p
                      css={css`
                        font-size: 14px;
                        color: ${getColor(vars.colors.content2)};
                        padding-right: 48px;
                      `}
                    >
                      Build trust and credibility with your customers
                    </p>
                  </div>
                  <Switch
                    checked={testimonialEnabled}
                    onChange={() => setTestimonialEnabled(!testimonialEnabled)}
                  />
                </div>

                <AnimatePresence>
                  {testimonialEnabled && (
                    <motion.div {...collapseAnim}>
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          gap: 16px;
                          padding-top: 16px;
                        `}
                      >
                        <Textarea
                          placeholder="Testimonial text"
                          value={testimonialText}
                          onChange={e => setTestimonialText(e.target.value)}
                          rows={3}
                        />
                        <TextInput
                          label="Customer name"
                          placeholder="Flolo D."
                          value={testimonialName}
                          onChange={e => setTestimonialName(e.target.value)}
                        />
                        <div>
                          <p
                            css={css`
                              font-size: 14px;
                              margin-bottom: 8px;
                              color: ${getColor(vars.colors.content)};
                            `}
                          >
                            Add image from
                          </p>
                          <div
                            css={css`
                              margin-bottom: 8px;
                            `}
                          >
                            <Select
                              options={imageSourceOptions}
                              value="computer"
                              onChange={() => {}}
                            />
                          </div>
                          <ImageUpload
                            image={testimonialImage}
                            height={200}
                            onUpload={url => setTestimonialImage(url)}
                            inputRef={testimonialUploadRef}
                          />
                        </div>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>

              <hr
                css={css`
                  border: none;
                  border-top: 1px solid ${getColor(vars.colors.border)};
                  margin: 0;
                `}
              />

              {/* Bump offers toggle */}
              <div onFocusCapture={() => setActiveSection('bump')}>
                <div
                  css={css`
                    display: flex;
                    gap: 16px;
                    align-items: flex-start;
                  `}
                >
                  <div
                    css={css`
                      flex: 1;
                    `}
                  >
                    <p
                      css={css`
                        font-size: 14px;
                        font-weight: 500;
                        color: ${getColor(vars.colors.content)};
                      `}
                    >
                      Bump offers
                    </p>
                    <p
                      css={css`
                        font-size: 14px;
                        color: ${getColor(vars.colors.content2)};
                      `}
                    >
                      Drive up to 30% more revenue by offering add-ons
                    </p>
                  </div>
                  <Switch checked={bumpEnabled} onChange={() => setBumpEnabled(!bumpEnabled)} />
                </div>

                <AnimatePresence>
                  {bumpEnabled && (
                    <motion.div {...collapseAnim}>
                      <div
                        css={css`
                          display: flex;
                          flex-direction: column;
                          gap: 16px;
                          padding-top: 16px;
                        `}
                      >
                        {!bumpSelected ? (
                          <button
                            type="button"
                            onClick={() => setShowProductModal(true)}
                            css={css`
                              width: 100%;
                              height: 40px;
                              padding: 0 12px;
                              border-radius: 6px;
                              border: 1px solid ${getColor(vars.colors.border)};
                              display: flex;
                              align-items: center;
                              justify-content: space-between;
                              font-size: 14px;
                              cursor: pointer;
                              background: none;
                            `}
                          >
                            <span
                              css={css`
                                color: ${getColor(vars.colors.content2)};
                              `}
                            >
                              Select a product
                            </span>
                            <svg width="10" height="18" viewBox="0 0 10 18" fill="none">
                              <path
                                d="M1 7L5 11L9 7"
                                stroke="#10100f"
                                strokeWidth="1.5"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              />
                            </svg>
                          </button>
                        ) : (
                          <>
                            <div
                              css={css`
                                width: 100%;
                                height: 40px;
                                padding: 0 6px;
                                border-radius: 6px;
                                border: 1px solid ${getColor(vars.colors.border)};
                                display: flex;
                                align-items: center;
                                font-size: 14px;
                              `}
                            >
                              <div
                                css={css`
                                  display: flex;
                                  align-items: center;
                                  gap: 8px;
                                  flex: 1;
                                  padding: 0 6px;
                                `}
                              >
                                <div
                                  css={css`
                                    width: 18px;
                                    height: 18px;
                                    border-radius: 2px;
                                    background: ${getColor(vars.colors.background3)};
                                    flex-shrink: 0;
                                  `}
                                />
                                <span
                                  css={css`
                                    font-size: 14px;
                                    color: ${getColor(vars.colors.content)};
                                    overflow: hidden;
                                    text-overflow: ellipsis;
                                    white-space: nowrap;
                                  `}
                                >
                                  {bumpProduct}
                                </span>
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  setBumpProduct('');
                                  setBumpSelected(false);
                                }}
                                css={css`
                                  flex-shrink: 0;
                                  padding: 0 6px;
                                  font-size: 18px;
                                  color: ${getColor(vars.colors.content2)};
                                  background: transparent;
                                  border: none;
                                  cursor: pointer;
                                  line-height: 1;
                                  font-family: inherit;
                                `}
                              >
                                &times;
                              </button>
                            </div>
                            <TextInput
                              label="Offer title"
                              placeholder="Bonus offer"
                              value={bumpOfferTitle}
                              onChange={e => setBumpOfferTitle(e.target.value)}
                            />
                          </>
                        )}
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            </div>
          </section>

          {/* Post-purchase confirmation */}
          <section
            onFocusCapture={() => setActiveSection('confirm')}
            css={css`
              margin-top: 12px;
              overflow: hidden;
              border-radius: 16px;
              box-shadow:
                0px 1px 1px -0.1px rgba(0, 0, 0, 0.05),
                0px 1.98px 3.06px -0.36px rgba(0, 0, 0, 0.05);
            `}
          >
            <div
              css={css`
                background: white;
                border-radius: 16px 16px 0 0;
                padding: 32px 40px 40px;

                label[class*='FieldLabel'],
                [class*='FieldLabelRoot'] {
                  margin-bottom: 8px;
                }
              `}
            >
              <h2 css={sectionTitleStyle}>Post-purchase confirmation</h2>
              <p
                css={css`
                  font-size: 14px;
                  margin-top: -16px;
                  margin-bottom: 24px;
                  color: ${getColor(vars.colors.content2)};
                `}
              >
                This is displayed after customers purchase your product
              </p>

              <div
                css={css`
                  margin-bottom: 24px;
                `}
              >
                <TextInput
                  label="Message title"
                  placeholder="How to cook: E-book"
                  value={confirmTitle}
                  onChange={e => setConfirmTitle(e.target.value)}
                />
              </div>
              <div
                css={css`
                  margin-bottom: 24px;
                `}
              >
                <Textarea
                  label="Description"
                  placeholder="You will soon receive an email with the receipt and instructions for accessing How to cook: E-book to email@address.com"
                  value={confirmDesc}
                  onChange={e => setConfirmDesc(e.target.value)}
                  rows={3}
                />
              </div>
              <div
                css={css`
                  margin-bottom: 24px;
                `}
              >
                <TextInput
                  label="Button text"
                  placeholder="Download now"
                  value={confirmBtnText}
                  onChange={e => setConfirmBtnText(e.target.value)}
                />
              </div>
              <div>
                <TextInput
                  label="Button link"
                  hint="We recommend linking directly to your website, hosted file, scheduling page, or member site"
                  type="url"
                  placeholder="https://yourdownload.com"
                />
              </div>
            </div>
            <div
              css={css`
                display: flex;
                align-items: center;
                justify-content: center;
                height: 48px;
                font-size: 12px;
                background: ${getColor(vars.colors.background3)};
                color: #10100f;
              `}
            >
              You can set the styling of your confirmation page in your{' '}
              <button
                type="button"
                css={css`
                  background: none;
                  border: none;
                  padding: 0;
                  cursor: pointer;
                  text-decoration: underline;
                  margin-left: 4px;
                  color: inherit;
                  font: inherit;
                `}
              >
                Product settings
              </button>
            </div>
          </section>

          {/* Testimonial section removed — now part of Selling tools above */}

          {/* Advanced */}
          <section
            css={css`
              ${sectionStyle.styles};
              margin-top: 12px;
              margin-bottom: 32px;
            `}
          >
            <h2 css={sectionTitleStyle}>Advanced</h2>

            <div
              css={css`
                display: flex;
                flex-direction: column;
                gap: 24px;
              `}
            >
              {/* Product link */}
              <div>
                <div
                  css={css`
                    display: flex;
                    gap: 8px;
                    align-items: flex-end;
                  `}
                >
                  <div
                    css={css`
                      flex: 1;
                    `}
                  >
                    <TextInput
                      label="Product link"
                      value="sjoerd.myflodesk.com / pho83h1d2y"
                      isReadOnly
                    />
                  </div>
                  <button
                    type="button"
                    aria-label="Edit"
                    css={css`
                      width: 36px;
                      height: 36px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      padding: 8px;
                      border-radius: 3px;
                      border: none;
                      background: none;
                      cursor: pointer;
                      flex-shrink: 0;
                      margin-bottom: 2px;
                    `}
                  >
                    <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
                      <path
                        d="M11.5 1.5L14.5 4.5M1 13L0.5 17L4.5 16.5L15 6L12 3L1 13Z"
                        stroke="#10100f"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              {/* Meta Pixel ID */}
              <div>
                <div
                  css={css`
                    display: flex;
                    gap: 8px;
                    align-items: flex-end;
                  `}
                >
                  <div
                    css={css`
                      flex: 1;
                    `}
                  >
                    <TextInput
                      label="Meta Pixel ID"
                      hint={
                        <>
                          Track visitor actions and ad performance.{' '}
                          <button
                            type="button"
                            css={css`
                              background: none;
                              border: none;
                              padding: 0;
                              cursor: pointer;
                              text-decoration: underline;
                              color: inherit;
                              font: inherit;
                            `}
                          >
                            Learn more
                          </button>
                        </>
                      }
                      value="sjoerd.myflodesk.com / pho83h1d2y"
                      isReadOnly
                    />
                  </div>
                  <button
                    type="button"
                    aria-label="Edit"
                    css={css`
                      width: 36px;
                      height: 36px;
                      display: flex;
                      align-items: center;
                      justify-content: center;
                      padding: 8px;
                      border-radius: 3px;
                      border: none;
                      background: none;
                      cursor: pointer;
                      flex-shrink: 0;
                      margin-bottom: 2px;
                    `}
                  >
                    <svg width="16" height="18" viewBox="0 0 16 18" fill="none">
                      <path
                        d="M11.5 1.5L14.5 4.5M1 13L0.5 17L4.5 16.5L15 6L12 3L1 13Z"
                        stroke="#10100f"
                        strokeWidth="1.2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                      />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </section>
        </div>

        {/* ── Right preview panel ── */}
        <PreviewPanel
          state={previewState}
          productName={productName}
          productDesc={productDesc}
          productImage={productImage}
          price={isRecurring ? recurringPrice : priceInput}
          salePrice={salePriceInput}
          onSale={onSaleActive}
          isRecurring={isRecurring}
          bumpProduct={bumpProduct}
          bumpOfferTitle={bumpOfferTitle}
          confirmTitle={confirmTitle}
          confirmDesc={confirmDesc}
          confirmBtnText={confirmBtnText}
          testimonialName={testimonialName}
          testimonialText={testimonialText}
          testimonialImage={testimonialImage}
        />
      </div>

      {showProductModal && (
        <ProductSelectModal
          onSelect={name => {
            setBumpProduct(name);
            setBumpSelected(true);
            setShowProductModal(false);
          }}
          onCancel={() => setShowProductModal(false)}
        />
      )}
    </div>
  );
}
